import { useState } from 'react';
import { toast } from 'sonner';
import { STATIC_FIELD_TYPES, conditionMet, type FormFieldDef } from '@hcc/types';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api';
import { useFormVersion } from '../../hooks/useOnboardingForms';
import { useAmendFormValues, type FormSubmissionEntry } from '../../hooks/onboardingOps';
import { FieldView } from './formfields/FieldView';
import type { FieldValue } from './formfields/formValues';

/** Answers HR cannot correct here: file-backed captures and layout fields. */
const UNEDITABLE = new Set(['file_upload', 'signature', 'damage_markup']);

function editableFields(pages: { fields: FormFieldDef[]; visible_if?: unknown }[], values: Record<string, FieldValue>) {
  return pages
    .filter((p) => conditionMet(p.visible_if as never, values))
    .flatMap((p) => p.fields)
    .filter(
      (f) =>
        !STATIC_FIELD_TYPES.includes(f.type) &&
        !UNEDITABLE.has(f.type) &&
        conditionMet(f.visible_if as never, values),
    );
}

/**
 * HR edit of a submitted form's answers, for correcting mistakes that
 * block downstream processing (SAP push postcodes and the like). Values
 * only: signatures, uploads and the signed PDF snapshot stay exactly as
 * the inductee submitted them, and every save is audited on the person's
 * event chain. Server-side validation gives the authoritative rejection;
 * this modal just edits and reports.
 */
export function AmendAnswersModal({
  personId,
  entry,
  onClose,
}: {
  personId: string;
  entry: FormSubmissionEntry;
  onClose: () => void;
}) {
  const version = useFormVersion(entry.form_version_id ?? undefined);
  const amend = useAmendFormValues(personId);
  const original = entry.values as Record<string, FieldValue>;
  const [values, setValues] = useState<Record<string, FieldValue>>(() => ({ ...original }));

  const fields = editableFields(version.data?.schema.pages ?? [], values);
  const changed = fields
    .map((f) => f.key)
    .filter((k) => JSON.stringify(values[k] ?? null) !== JSON.stringify(original[k] ?? null));

  function onSave() {
    const patch: Record<string, unknown> = {};
    for (const k of changed) patch[k] = values[k] ?? null;
    amend.mutate(
      { formId: entry.form_id, values: patch },
      {
        onSuccess: (res) => {
          toast.success(
            `Corrected ${res.changed_keys.length} answer${res.changed_keys.length === 1 ? '' : 's'} — change recorded in the audit trail`,
          );
          onClose();
        },
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not save the correction'),
      },
    );
  }

  return (
    <ModalShell
      open
      onClose={onClose}
      titleId="amend-answers-title"
      maxWidth="max-w-2xl"
      busy={amend.isPending}
    >
      <div className="space-y-4 p-5">
        <div>
          <h2 id="amend-answers-title" className="text-lg font-semibold text-fg">
            Edit answers — {entry.title}
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            For correcting mistakes in what was submitted. Every change is recorded
            against this person&apos;s record; the signed PDF keeps the original answers.
          </p>
        </div>

        {version.isLoading ? (
          <p className="text-sm text-fg-muted">Loading form…</p>
        ) : fields.length === 0 ? (
          <p className="text-sm text-fg-muted">This form has no editable answers.</p>
        ) : (
          <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
            {fields.map((f) => (
              <FieldView
                key={f.key}
                field={f}
                value={values[f.key] ?? null}
                onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
              />
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-line/10 pt-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={amend.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={amend.isPending}
            disabled={changed.length === 0}
            onClick={onSave}
          >
            Save {changed.length > 0 ? `${changed.length} change${changed.length === 1 ? '' : 's'}` : 'changes'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
